import React, { useState, useEffect, useContext } from 'react';

import { AnalyticsContext } from '../../../ModularReduxAnalytics';
import { useApp, useAppDispatchers } from '../../../state/app';
import { useToggles } from '../../../state/toggles';

import { WIDGET_NAME } from './shared';

import DebitContainer from './Debit/container';
import ManualAchContainer from './ManualACH/container';
import MorganStanley from './MorganStanley';
import Plaid from './Plaid';

import css from './FundingAccount.module.scss';
import CreditMessage from './CreditMessage';
import { planPrices } from '../../../utils/prices';

type Answers = { [key: string]: number };

export interface FundingAccountProps {
  questionsAndAnswers: Answers;
  resolvesErrorId?: number;
  onClick?: (e) => void;
}

function renderPaymentMethod(
  container: 'debit' | 'manualACH',
  {
    fundingSource,
    setFundingSource,
    getInitialFundingAmount,
    submitPaymentMethod,
    shouldRenderManualACH,
    shouldRenderDebit
  }
) {
  if (container === 'debit' && shouldRenderDebit)
    return (
      <DebitContainer
        fundingSource={fundingSource}
        setFundingSource={setFundingSource}
        getInitialFundingAmount={getInitialFundingAmount}
        submitPaymentMethod={submitPaymentMethod}
      />
    );
  if (container === 'manualACH' && shouldRenderManualACH)
    return (
      <ManualAchContainer
        fundingSource={fundingSource}
        setFundingSource={setFundingSource}
        getInitialFundingAmount={getInitialFundingAmount}
        submitPaymentMethod={submitPaymentMethod}
      />
    );
}

export default function FundingAccount({
  onClick,
  questionsAndAnswers,
  resolvesErrorId
}: FundingAccountProps) {
  const { analytics } = useContext(AnalyticsContext);
  const {
    hideFunding,
    reOrderDebitOnFundingScreen,
    enableGSRChange,
    enableCorePriceChange
  } = useToggles();
  const { registrationToken, answers, flowId } = useApp();
  const { getAnswers, queryGetInitialFunding } = useAppDispatchers();

  const [fundingSource, setFundingSource] = useState<string | null>(null);
  const [showPlaid, setShowPlaid] = useState<boolean>(true);

  const plans = planPrices({ enableCorePriceChange, enableGSRChange });

  useEffect(() => {
    getAnswers();
    queryGetInitialFunding();

    analytics.trackEvent('Registration Add Funding Account Viewed');
    analytics.trackEvent('Feature Toggle Decided', {
      eventProps: {
        feature_key: 'acquisition_reOrderDebitOnFundingScreen',
        variation_key: reOrderDebitOnFundingScreen.variationKey,
        toggle_enabled: reOrderDebitOnFundingScreen.enabled,
        experiment_key: 'acquisition_reOrderDebitOnFundingScreen_test'
      }
    });

    if (hideFunding?.hidePlaid) {
      setShowPlaid(false);

      if (hideFunding.hideManualACH) {
        // If hiding both plaid and manual ach, lets go ahead and open debit form
        setFundingSource('debit');
      }
    }
  }, []);

  const getInitialFundingAmount = () =>
    answers?.setInitialFundingAmount ||
    questionsAndAnswers?.set_initial_funding_amount;

  const submitPaymentMethod = (accountDetails, callback) => {
    const questions = [...Object.keys(questionsAndAnswers), WIDGET_NAME];
    const currentAnswers = [
      ...Object.values(questionsAndAnswers),
      accountDetails
    ];
    callback({
      accessToken: registrationToken,
      questions,
      answers: currentAnswers,
      resolveErrorId: resolvesErrorId
    });
  };

  const showCreditMessage = !answers?.setInitialFundingAmount;

  const isMorganStanleyFlow = flowId === 'parent_flow_14';

  const hideManualACH = hideFunding?.hideManualACH;

  const shouldRenderDebit = !hideFunding?.hideDebit && !showCreditMessage;
  const shouldRenderManualACH =
    !isMorganStanleyFlow && (!hideManualACH || fundingSource === 'ach');

  const renderPaymentMethodParams = {
    fundingSource,
    setFundingSource,
    getInitialFundingAmount,
    submitPaymentMethod,
    shouldRenderDebit,
    shouldRenderManualACH
  };

  const debitAchComponentsToRender = reOrderDebitOnFundingScreen.enabled
    ? (['debit', 'manualACH'] as const)
    : (['manualACH', 'debit'] as const);

  return (
    <div onClick={onClick}>
      {isMorganStanleyFlow && (
        <MorganStanley
          fundingSource={fundingSource}
          hideManualACH={hideManualACH}
          showPlaid={showPlaid}
          setShowPlaid={setShowPlaid}
          setFundingSource={setFundingSource}
          getInitialFundingAmount={getInitialFundingAmount}
          submitPaymentMethod={submitPaymentMethod}
         fee={plans.standard}/>
      )}
      <div className={css.AddFundingAccountMainBox}>
        <div data-e2e="manual-funding-account">
          {!isMorganStanleyFlow && showPlaid && (
            <>
              <Plaid
                isMorganStanleyFlow={isMorganStanleyFlow}
                setShowPlaid={setShowPlaid}
                setFundingSource={setFundingSource}
                getInitialFundingAmount={getInitialFundingAmount}
                submitPaymentMethod={submitPaymentMethod}
              />
            </>
          )}

          {debitAchComponentsToRender.map(componentToRender =>
            renderPaymentMethod(componentToRender, renderPaymentMethodParams)
          )}

          {showCreditMessage && <CreditMessage />}
        </div>
      </div>
    </div>
  );
}
